import React, { useState, useMemo } from "react";
import { useQuery, useApolloClient } from "@apollo/client";
import {
  Row,
  Col,
  Typography,
  Table,
  Button,
  Input,
  Space,
  Card,
} from "antd";
import {
  FileExcelFilled,
  SearchOutlined,
} from "@ant-design/icons";
import { format, subDays } from "date-fns";
import withClinic from "../../hooks/with_clinic";
import DateRange from "../../components/date_range";
import HorizontalScrollContainer from "../../components/horizontal_scroll_container";
import {
  ApolloResult,
  Clinic,
  MainPaymentViewResult,
} from "../../store";
import {
  get_sales_by_sales_person,
  get_sales_by_sales_person_summary,
  getSalesBySalesPersonVar,
  getSalesBySalesPersonSummaryVar,
} from "../../graphql/order";
import LoadingSpinner from "../../components/loading_indicator";
import Error from "../../components/apollo_error";
import { formatMoney, getCurrency } from "../../helpers/utils";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";
import { PaginationConfig } from "antd/lib/pagination";


interface P {
  currentClinic?: Clinic;
}

interface Transaction extends MainPaymentViewResult {
  CustomerPhoneNumber: string;
  SellerName: string;
  key?: string;
}

interface SalesPersonData {
  salesPerson: string;
  transactionCount: number;
  totalAmount: number;
  key?: string;
}

interface SalesBySalesPersonResponse {
  data: Transaction[];
  totalCount: number;
}

interface SalesBySalesPersonSummaryItem {
  SellerName: string;
  rowCount: number;
  total: number;
}

interface SalesBySalesPersonSummaryResponse {
  data: SalesBySalesPersonSummaryItem[];
}

const hasRowLevelPaymentMethod = (record: MainPaymentViewResult) =>
  !!record.PaymentMethod?.trim();

const hasRowLevelPaymentAmount = (record: MainPaymentViewResult) =>
  record.PaymentAmount != null && record.PaymentAmount !== 0;

const buildInvoicesWithRowPaymentMethod = (rows: MainPaymentViewResult[]) => {
  const invoiceSet = new Set<string>();
  rows.forEach((record) => {
    if (hasRowLevelPaymentMethod(record) && record.InvoiceNumber) {
      invoiceSet.add(record.InvoiceNumber);
    }
  });
  return invoiceSet;
};

const buildInvoicesWithRowPaymentAmount = (rows: MainPaymentViewResult[]) => {
  const invoiceSet = new Set<string>();
  rows.forEach((record) => {
    if (hasRowLevelPaymentAmount(record) && record.InvoiceNumber) {
      invoiceSet.add(record.InvoiceNumber);
    }
  });
  return invoiceSet;
};

const getDisplayPaymentMethod = (
  record: MainPaymentViewResult,
  invoicesWithRowPaymentMethod: Set<string>
) => {
  if (hasRowLevelPaymentMethod(record)) {
    return record.PaymentMethod.trim();
  }

  const invoiceNumber = record.InvoiceNumber || "";
  if (invoiceNumber && invoicesWithRowPaymentMethod.has(invoiceNumber)) {
    return "";
  }

  return record.OrderPaymentMethod?.trim() || "";
};

const getDisplayPaymentAmount = (
  record: MainPaymentViewResult,
  invoicesWithRowPaymentAmount: Set<string>
): number | null => {
  if (hasRowLevelPaymentAmount(record)) {
    return record.PaymentAmount;
  }

  const invoiceNumber = record.InvoiceNumber || "";
  if (invoiceNumber && invoicesWithRowPaymentAmount.has(invoiceNumber)) {
    return null;
  }

  const orderNetTotal = record.OrderNetTotal;
  return orderNetTotal == null || orderNetTotal === 0 ? null : orderNetTotal;
};

const SalesBySalesPersonPage = (props: P) => {
  const clinicCode = props.currentClinic?.code || "";
  const client = useApolloClient();
  const [pagination, setPagination] = useState<PaginationConfig | null>({
    current: 1,
    pageSize: 20,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50", "100"],
  });
  const [exporting, setExporting] = useState(false);
  const [ranges, setRanges] = useState({
    startDate: subDays(new Date(), 7),
    endDate: new Date(),
  });
  const [searchTerm, setSearchTerm] = useState("");

  const take = pagination?.pageSize || 20;
  const skip = ((pagination?.current || 1) - 1) * take;

  const variables = getSalesBySalesPersonVar({
    clinicCode,
    startDate: ranges.startDate,
    endDate: ranges.endDate,
    skip,
    take,
  });

  const summaryVariables = getSalesBySalesPersonSummaryVar({
    clinicCode,
    startDate: ranges.startDate,
    endDate: ranges.endDate,
  });

  const {
    loading: summaryLoading,
    data: summaryData,
    error: summaryError,
  } = useQuery<
    ApolloResult<"getSalesBySalesPersonSummary", SalesBySalesPersonSummaryResponse>
  >(get_sales_by_sales_person_summary, {
    variables: summaryVariables,
    skip: !clinicCode,
    fetchPolicy: "network-only",
  });

  const { loading, data, error } = useQuery<
    ApolloResult<"getSalesBySalesPerson", SalesBySalesPersonResponse>
  >(get_sales_by_sales_person, {
    variables,
    skip: !clinicCode,
    fetchPolicy: "network-only",
  });

  const invoicesWithRowPaymentMethod = useMemo(
    () => buildInvoicesWithRowPaymentMethod(data?.getSalesBySalesPerson?.data || []),
    [data?.getSalesBySalesPerson?.data]
  );

  const invoicesWithRowPaymentAmount = useMemo(
    () => buildInvoicesWithRowPaymentAmount(data?.getSalesBySalesPerson?.data || []),
    [data?.getSalesBySalesPerson?.data]
  );

  const salesBySalesPersonData = useMemo(() => {
    if (!summaryData?.getSalesBySalesPersonSummary?.data) return [];

    return summaryData.getSalesBySalesPersonSummary.data.map((item) => ({
      key: item.SellerName || "Anyone",
      salesPerson: item.SellerName || "Anyone",
      transactionCount: item.rowCount,
      totalAmount: item.total,
    }));
  }, [summaryData?.getSalesBySalesPersonSummary?.data]);

  // Filter transactions based on search and selected sales person
  const filteredTransactions = useMemo(() => {
    if (!data?.getSalesBySalesPerson?.data) return [];

    let filtered = data.getSalesBySalesPerson.data;

    // Apply search filter
    if (searchTerm.trim() !== "") {
      const normalizedSearchTerm = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (record: Transaction) =>
          (record.InvoiceNumber?.toLowerCase().includes(normalizedSearchTerm) ||
            false) ||
          (record.CustomerName?.toLowerCase().includes(normalizedSearchTerm) ||
            false) ||
          (record.CustomerPhoneNumber?.toLowerCase().includes(
            normalizedSearchTerm
          ) ||
            false) ||
          (record.SellerName?.toLowerCase().includes(normalizedSearchTerm) ||
            false) ||
          (record.ServiceName?.toLowerCase().includes(normalizedSearchTerm) ||
            false) ||
          (record.ServicePackageName?.toLowerCase().includes(
            normalizedSearchTerm
          ) ||
            false)
      );
    }

    return filtered.map((t: Transaction, index: number) => ({
      ...t,
      key: `${t.InvoiceNumber}-${index}`,
    }));
  }, [
    data?.getSalesBySalesPerson?.data,
    searchTerm,
  ]);

  // Calculate totals
  const totalTransactions = salesBySalesPersonData.reduce(
    (sum, person) => sum + person.transactionCount,
    0
  );
  const totalAmount = salesBySalesPersonData.reduce(
    (sum, person) => sum + person.totalAmount,
    0
  );


  // Combined export function
  const exportToExcel = async () => {
    if (salesBySalesPersonData.length === 0 && filteredTransactions.length === 0) return;
    setExporting(true);

    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";

    try {
      // Fetch all transactions for export (not paginated)
      const exportVariables = getSalesBySalesPersonVar({
        clinicCode,
        startDate: ranges.startDate,
        endDate: ranges.endDate,
        skip: 0,
        take: 100000, // Large number to get all records
      });

      const exportResult = await client.query<
        ApolloResult<"getSalesBySalesPerson", SalesBySalesPersonResponse>
      >({
        query: get_sales_by_sales_person,
        variables: exportVariables,
        fetchPolicy: "network-only",
      });

      const allTransactions = exportResult.data?.getSalesBySalesPerson?.data || [];

      // Apply search filter to all transactions
      const normalizedSearchTerm = searchTerm.toLowerCase().trim();
      const exportTransactions = allTransactions.filter((record: Transaction) => {
        if (searchTerm.trim() === "") return true;
        return (
          (record.InvoiceNumber?.toLowerCase().includes(normalizedSearchTerm) ||
            false) ||
          (record.CustomerName?.toLowerCase().includes(normalizedSearchTerm) ||
            false) ||
          (record.CustomerPhoneNumber?.toLowerCase().includes(
            normalizedSearchTerm
          ) || false) ||
          (record.SellerName?.toLowerCase().includes(normalizedSearchTerm) ||
            false) ||
          (record.ServiceName?.toLowerCase().includes(normalizedSearchTerm) ||
            false) ||
          (record.ServicePackageName?.toLowerCase().includes(
            normalizedSearchTerm
          ) || false)
        );
      });

      // Prepare summary data
      const summaryRows = [
        ...salesBySalesPersonData.map((person) => ({
          "Sales Person": person.salesPerson,
          "Transaction Count": person.transactionCount,
          "Total Amount": person.totalAmount === 0 ? "" : person.totalAmount,
        })),
        {
          "Sales Person": "Total",
          "Transaction Count": totalTransactions,
          "Total Amount": totalAmount === 0 ? "" : totalAmount,
        },
      ];

      const exportInvoicesWithRowPaymentMethod =
        buildInvoicesWithRowPaymentMethod(allTransactions);
      const exportInvoicesWithRowPaymentAmount =
        buildInvoicesWithRowPaymentAmount(allTransactions);

      // Prepare transactions data from all pages
      const transactionRows = exportTransactions.map((transaction) => ({
        Date: transaction.Date,
        "Sales Person": transaction.SellerName || "-",
        Invoice: transaction.InvoiceNumber,
        "Customer Name": transaction.CustomerName,
        "Customer Phone": transaction.CustomerPhoneNumber || "-",
        Service: transaction.ServiceName,
        Package: transaction.ServicePackageName || "-",
        "Payment Method": getDisplayPaymentMethod(
          transaction,
          exportInvoicesWithRowPaymentMethod
        ) || "-",
        "Payment Amount": (() => {
          const amount = getDisplayPaymentAmount(
            transaction,
            exportInvoicesWithRowPaymentAmount
          );
          return amount == null ? "" : amount;
        })(),
        "Net Total":
          Number(transaction.InvoiceNetTotal) === 0
            ? ""
            : Number(transaction.InvoiceNetTotal),
      }));

      // Create worksheets
      const summaryWs = XLSX.utils.json_to_sheet(summaryRows);
      const transactionWs = XLSX.utils.json_to_sheet(transactionRows);

      // Create workbook with multiple sheets
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, summaryWs, "Sales Summary");
      XLSX.utils.book_append_sheet(wb, transactionWs, "Detailed Transactions");

      // Generate filename with current date
      const dateStr = format(new Date(), "yyyy-MM-dd");
      const fileName = `sales_by_sales_person_${dateStr}`;

      // Export file
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(data, fileName + fileExtension);
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setExporting(false);
    }
  };

  // Table columns for sales summary
  const summaryColumns = [
    {
      title: "Sales Person",
      dataIndex: "salesPerson",
      key: "salesPerson",
      width: 200,
    },
    {
      title: "Transaction Count",
      dataIndex: "transactionCount",
      key: "transactionCount",
      width: 150,
      align: "right" as const,
      sorter: (a: SalesPersonData, b: SalesPersonData) =>
        a.transactionCount - b.transactionCount,
      render: (v: number) => v.toLocaleString(),
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 150,
      align: "right" as const,
      sorter: (a: SalesPersonData, b: SalesPersonData) =>
        a.totalAmount - b.totalAmount,
      render: (v: number) =>
        v === 0
          ? "-"
          : `${formatMoney(v)} ${getCurrency(props.currentClinic)}`,
    },
  ];

  // Table columns for detailed transactions
  const transactionColumns = [
    {
      title: "Date",
      dataIndex: "Date",
      key: "Date",
      width: 120,
      sorter: (a: Transaction, b: Transaction) =>
        new Date(a.Date).getTime() - new Date(b.Date).getTime(),
    },
    {
      title: "Sales Person",
      dataIndex: "SellerName",
      key: "SellerName",
      width: 150,
      ellipsis: true,
      render: (v: string | null) => v || "-",
    },
    {
      title: "Invoice",
      dataIndex: "InvoiceNumber",
      key: "InvoiceNumber",
      width: 130,
    },
    {
      title: "Customer Name",
      dataIndex: "CustomerName",
      key: "CustomerName",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Customer Phone",
      dataIndex: "CustomerPhoneNumber",
      key: "CustomerPhoneNumber",
      width: 130,
      ellipsis: true,
      render: (v: string | null) => v || "-",
    },
    {
      title: "Service",
      dataIndex: "ServiceName",
      key: "ServiceName",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Package",
      dataIndex: "ServicePackageName",
      key: "ServicePackageName",
      width: 150,
      ellipsis: true,
      render: (v: string | null) => v || "-",
    },
    {
      title: "Payment Method",
      dataIndex: "PaymentMethod",
      key: "PaymentMethod",
      width: 130,
      ellipsis: true,
      render: (_: string | null, record: Transaction) =>
        getDisplayPaymentMethod(record, invoicesWithRowPaymentMethod) || "-",
    },
    {
      title: "Payment Amount",
      dataIndex: "PaymentAmount",
      key: "PaymentAmount",
      width: 130,
      align: "right" as const,
      sorter: (a: Transaction, b: Transaction) =>
        (getDisplayPaymentAmount(a, invoicesWithRowPaymentAmount) || 0) -
        (getDisplayPaymentAmount(b, invoicesWithRowPaymentAmount) || 0),
      render: (_: number, record: Transaction) => {
        const amount = getDisplayPaymentAmount(
          record,
          invoicesWithRowPaymentAmount
        );
        return amount == null
          ? "-"
          : `${formatMoney(amount)} ${getCurrency(props.currentClinic)}`;
      },
    },
    {
      title: "Net Total",
      dataIndex: "InvoiceNetTotal",
      key: "InvoiceNetTotal",
      width: 130,
      align: "right" as const,
      sorter: (a: Transaction, b: Transaction) =>
        a.InvoiceNetTotal - b.InvoiceNetTotal,
      render: (v: number) =>
        v === 0
          ? "-"
          : `${formatMoney(v)} ${getCurrency(props.currentClinic)}`,
    },
  ];

  const onDateChange = (startDate: Date, endDate: Date) => {
    setRanges({ startDate, endDate });
  };

  // Control section
  const control = (
    <Row style={{ marginTop: -18 }}>
      <Col flex={4}>
        <Row>
          <Col span={8}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Sales by Sales Person
            </Typography.Title>
          </Col>
        </Row>
      </Col>
      <Col flex={0}>
        <Space>
          <Input
            placeholder="Search..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <DateRange
            startDate={ranges.startDate}
            endDate={ranges.endDate}
            onSelect={onDateChange}
          />
        </Space>
      </Col>
    </Row>
  );

  // Filters section
  const filters = (
    <Row style={{ marginTop: 4, marginRight: 2 }}>
      <Col span={24} style={{ textAlign: "right" }}>
        <Button
          size="small"
          loading={exporting}
          icon={<FileExcelFilled />}
          onClick={exportToExcel}
        >
          Export
        </Button>
      </Col>
    </Row>
  );

  // Summary table
  const summaryTable = (
    <Card
      title={`Sales Summary (${salesBySalesPersonData.length} sales persons)`}
      style={{ marginBottom: 16 }}
    >
      {salesBySalesPersonData.length > 0 ? (
        <Table
          dataSource={salesBySalesPersonData}
          pagination={false}
          size="small"
          loading={summaryLoading}
          columns={summaryColumns}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <Typography.Text strong>Grand Total</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Typography.Text strong>
                    {totalTransactions.toLocaleString()}
                  </Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <Typography.Text strong>
                    {totalAmount === 0
                      ? "-"
                      : `${formatMoney(totalAmount)} ${getCurrency(
                          props.currentClinic
                        )}`}
                  </Typography.Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      ) : (
        <Typography.Text type="secondary">
          No sales data available for the selected date range
        </Typography.Text>
      )}
    </Card>
  );

  // Detailed transactions table
  const context = (
    <Row style={{ marginTop: 8 }}>
      <Col span={24}>
        <Card
          title={`Detailed Transactions (${filteredTransactions.length})`}
          style={{ marginBottom: 16 }}
        >
          <HorizontalScrollContainer>
            <Table
              showHeader={filteredTransactions.length > 0}
              dataSource={filteredTransactions}
              columns={transactionColumns}
              scroll={{ x: 1500 }}
              onChange={(p: PaginationConfig) => {
                setPagination(p);
              }}
              pagination={{
                total: data?.getSalesBySalesPerson?.totalCount || filteredTransactions.length,
                current: pagination?.current || 1,
                pageSize: pagination?.pageSize || 20,
                defaultPageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "30", "50", "100"],
              }}
              loading={loading}
            />
          </HorizontalScrollContainer>
        </Card>
      </Col>
    </Row>
  );

  if ((loading && !data) || (summaryLoading && !summaryData)) {
    return <LoadingSpinner />;
  }

  if (error || summaryError) {
    return <Error error={(error || summaryError)!} />;
  }

  if (!clinicCode) {
    return (
      <Card>
        <Typography.Text type="secondary">
          Please select a clinic first to view sales data.
        </Typography.Text>
      </Card>
    );
  }

  return (
    <React.Fragment>
      {control}
      {filters}
      {summaryTable}
      {context}
    </React.Fragment>
  );
};

export default withClinic(SalesBySalesPersonPage);
